import { Bot, ListTree, Sparkles } from "lucide-react";
import { FC } from "react";
import { FormattedMessage } from "react-intl";

export type PipelineMode = "USE_TOC" | "USE_SNOWFLAKE" | "USE_SMART_AGENT";

interface DisabledModeInfo {
  mode: PipelineMode;
  reason: string;
}

interface PipelineModeSelectorProps {
  selectedMode: PipelineMode;
  onModeChange: (mode: PipelineMode) => void;
  disabled?: boolean;
  variant?: "compact" | "detailed";
  availableModes?: PipelineMode[];
  disabledModes?: DisabledModeInfo[];
}

export const PipelineModeSelector: FC<PipelineModeSelectorProps> = ({
  selectedMode,
  onModeChange,
  disabled = false,
  variant = "compact",
  availableModes = ["USE_TOC", "USE_SNOWFLAKE", "USE_SMART_AGENT"],
  disabledModes = [],
}) => {
  const isModeDisabled = (mode: PipelineMode) => {
    return disabledModes.some((dm) => dm.mode === mode);
  };

  const getDisabledReason = (mode: PipelineMode) => {
    return disabledModes.find((dm) => dm.mode === mode)?.reason;
  };

  const handleModeSelect = (mode: PipelineMode) => {
    if (!disabled && !isModeDisabled(mode)) {
      onModeChange(mode);
    }
  };

  const padding = variant === "detailed" ? "p-4" : "p-3";
  const gap = variant === "detailed" ? "gap-3" : "gap-2";

  return (
    <div className="flex flex-col gap-2">
      <div className={`grid ${gap} ${disabled ? "opacity-50" : ""}`}>
        {availableModes.includes("USE_TOC") && (
          <div
            onClick={() => handleModeSelect("USE_TOC")}
            className={`
              relative flex cursor-pointer rounded-lg border ${padding} shadow-sm focus:outline-none
              ${selectedMode === "USE_TOC" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50 dark:bg-sky-950" : "border-border hover:bg-accent"}
              ${disabled || isModeDisabled("USE_TOC") ? "opacity-50 cursor-not-allowed bg-muted" : ""}
            `}
            role="button"
            tabIndex={disabled || isModeDisabled("USE_TOC") ? -1 : 0}
            onKeyDown={(e) => {
              if (
                (e.key === "Enter" || e.key === " ") &&
                !disabled &&
                !isModeDisabled("USE_TOC")
              ) {
                handleModeSelect("USE_TOC");
              }
            }}
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center">
                <div className="text-sm">
                  <div className="flex items-center gap-2 font-medium text-foreground">
                    <ListTree className="h-4 w-4 text-muted-foreground" />
                    <FormattedMessage
                      id="pipelineMode.useToc.title"
                      defaultMessage="Use Table of Contents"
                    />
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    <FormattedMessage
                      id={
                        variant === "detailed"
                          ? "pipelineMode.useToc.description.detailed"
                          : "pipelineMode.useToc.description"
                      }
                      defaultMessage={
                        variant === "detailed"
                          ? "Preserves the document's existing bookmarks and hierarchy. Best for well-organized documents."
                          : "Preserves the document's existing bookmarks and hierarchy. Best for well-organized documents."
                      }
                    />
                  </p>
                  {variant === "detailed" && (
                    <p className="text-muted-foreground/70 mt-1 text-xs">
                      <FormattedMessage
                        id="pipelineMode.useToc.examples"
                        defaultMessage="Technical documentation, Policy manuals"
                      />
                    </p>
                  )}
                  {variant === "detailed" && (
                    <span className="inline-block mt-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                      <FormattedMessage
                        id="pipelineMode.useToc.badge"
                        defaultMessage="Fastest processing"
                      />
                    </span>
                  )}
                  {isModeDisabled("USE_TOC") && (
                    <p className="text-red-500 text-xs font-semibold mt-1">
                      {getDisabledReason("USE_TOC")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {availableModes.includes("USE_SNOWFLAKE") && (
          <div
            onClick={() => handleModeSelect("USE_SNOWFLAKE")}
            className={`
              relative flex cursor-pointer rounded-lg border ${padding} shadow-sm focus:outline-none
              ${selectedMode === "USE_SNOWFLAKE" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50 dark:bg-sky-950" : "border-border hover:bg-accent"}
              ${disabled || isModeDisabled("USE_SNOWFLAKE") ? "opacity-50 cursor-not-allowed bg-muted" : ""}
            `}
            role="button"
            tabIndex={disabled || isModeDisabled("USE_SNOWFLAKE") ? -1 : 0}
            onKeyDown={(e) => {
              if (
                (e.key === "Enter" || e.key === " ") &&
                !disabled &&
                !isModeDisabled("USE_SNOWFLAKE")
              ) {
                handleModeSelect("USE_SNOWFLAKE");
              }
            }}
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center">
                <div className="text-sm">
                  <div className="flex items-center gap-2 font-medium text-foreground">
                    <Sparkles className="h-4 w-4 text-orange-500" />
                    <FormattedMessage
                      id="pipelineMode.useSnowflake.title"
                      defaultMessage="Use AI and Preset Categories"
                    />
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    <FormattedMessage
                      id={
                        variant === "detailed"
                          ? "pipelineMode.useSnowflake.description.detailed"
                          : "pipelineMode.useSnowflake.description"
                      }
                      defaultMessage={
                        variant === "detailed"
                          ? "AI assigns content to your organization's categories (Settings > Categories). Best for unstructured documents."
                          : "AI assigns content to your organization's categories (Settings > Categories). Best for unstructured documents."
                      }
                    />
                  </p>
                  {variant === "detailed" && (
                    <p className="text-muted-foreground/70 mt-1 text-xs">
                      <FormattedMessage
                        id="pipelineMode.useSnowflake.examples"
                        defaultMessage="Unstructured documents, Poor formatting, Legacy PDFs"
                      />
                    </p>
                  )}
                  {isModeDisabled("USE_SNOWFLAKE") && (
                    <p className="text-red-500 text-xs font-semibold mt-1">
                      {getDisabledReason("USE_SNOWFLAKE")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {availableModes.includes("USE_SMART_AGENT") && (
          <div
            onClick={() => handleModeSelect("USE_SMART_AGENT")}
            className={`
              relative flex cursor-pointer rounded-lg border ${padding} shadow-sm focus:outline-none
              ${selectedMode === "USE_SMART_AGENT" ? "border-sky-600 ring-1 ring-sky-600 bg-sky-50 dark:bg-sky-950" : "border-border hover:bg-accent"}
              ${disabled || isModeDisabled("USE_SMART_AGENT") ? "opacity-50 cursor-not-allowed bg-muted" : ""}
            `}
            role="button"
            tabIndex={disabled || isModeDisabled("USE_SMART_AGENT") ? -1 : 0}
            onKeyDown={(e) => {
              if (
                (e.key === "Enter" || e.key === " ") &&
                !disabled &&
                !isModeDisabled("USE_SMART_AGENT")
              ) {
                handleModeSelect("USE_SMART_AGENT");
              }
            }}
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center">
                <div className="text-sm">
                  <div className="flex items-center gap-2 font-medium text-foreground">
                    <Bot className="h-4 w-4 text-sky-500" />
                    <FormattedMessage
                      id="pipelineMode.useSmartAgent.title"
                      defaultMessage="Automatic"
                    />
                    <span className="inline-block px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                      <FormattedMessage
                        id="pipelineMode.useSmartAgent.recommended"
                        defaultMessage="Recommended"
                      />
                    </span>
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    <FormattedMessage
                      id={
                        variant === "detailed"
                          ? "pipelineMode.useSmartAgent.description.detailed"
                          : "pipelineMode.useSmartAgent.description"
                      }
                      defaultMessage={
                        variant === "detailed"
                          ? "For each document uploaded, evaluates quality and chooses between Table of Contents or AI with Preset Categories."
                          : "For each document uploaded, evaluates quality and chooses between Table of Contents or AI with Preset Categories."
                      }
                    />
                  </p>
                  {variant === "detailed" && (
                    <>
                      <div className="mt-2 text-xs text-muted-foreground">
                        <p className="font-medium">
                          <FormattedMessage
                            id="pipelineMode.useSmartAgent.detects"
                            defaultMessage="Quality signals detected:"
                          />
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          <li className="flex items-center gap-1">
                            <span className="text-red-500">x</span>
                            <FormattedMessage
                              id="pipelineMode.useSmartAgent.detectsArtifacts"
                              defaultMessage="Code artifacts (SEC_001)"
                            />
                          </li>
                          <li className="flex items-center gap-1">
                            <span className="text-red-500">x</span>
                            <FormattedMessage
                              id="pipelineMode.useSmartAgent.detectsSplits"
                              defaultMessage="Page splits (Page 1, 2)"
                            />
                          </li>
                          <li className="flex items-center gap-1">
                            <span className="text-green-500">&#10003;</span>
                            <FormattedMessage
                              id="pipelineMode.useSmartAgent.detectsHierarchies"
                              defaultMessage="Clear hierarchies"
                            />
                          </li>
                          <li className="flex items-center gap-1">
                            <span className="text-green-500">&#10003;</span>
                            <FormattedMessage
                              id="pipelineMode.useSmartAgent.detectsNames"
                              defaultMessage="Descriptive names"
                            />
                          </li>
                        </ul>
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        <p className="font-medium">
                          <FormattedMessage
                            id="pipelineMode.useSmartAgent.benefits"
                            defaultMessage="Benefits:"
                          />
                        </p>
                        <p>
                          <FormattedMessage
                            id="pipelineMode.useSmartAgent.benefitsList"
                            defaultMessage="Zero manual decisions, Adapts per document, Consistent quality, Review in Settings anytime"
                          />
                        </p>
                      </div>
                    </>
                  )}
                  {isModeDisabled("USE_SMART_AGENT") && (
                    <p className="text-red-500 text-xs font-semibold mt-1">
                      {getDisabledReason("USE_SMART_AGENT")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
